import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Loader2, RefreshCw, StopCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { API_URL } from '../lib/utils';

const PAGE_SIZE = 50;
const SEND_DELAY_SECONDS = 5;

const Stat = ({ value, label, className, testId }) => (
  <div className={`p-4 rounded-lg text-center ${className}`} data-testid={testId}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

const StatusCell = ({ r }) => {
  if (r.status === 'sent') return <Badge className="bg-green-100 text-green-700">Sent</Badge>;
  if (r.status === 'failed') {
    return (
      <div>
        <Badge className="bg-red-100 text-red-700">Failed</Badge>
        <p className="text-xs text-red-600 mt-1 max-w-xs break-words">{r.error || 'Reason not recorded'}</p>
      </div>
    );
  }
  return <Badge className="bg-slate-100 text-slate-600">Not sent yet</Badge>;
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [loadingRows, setLoadingRows] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/email-marketing/campaigns/${id}/stats`);
      setStats(data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Only admins can view campaign details');
        navigate('/');
        return;
      }
      toast.error('Failed to load campaign');
    }
  }, [id, navigate]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filter !== 'all') params.filter_status = filter;
      const { data } = await axios.get(`${API_URL}/email-marketing/campaigns/${id}/recipients`, { params });
      setRows(data);
    } catch {
      toast.error('Failed to load recipients');
    } finally {
      setLoadingRows(false);
    }
  }, [id, page, filter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => {
    if (stats?.status !== 'sending') return;
    const t = setInterval(() => { loadStats(); }, 15000);
    return () => clearInterval(t);
  }, [stats?.status, loadStats]);

  const toResend = stats?.resendable_count || 0;

  const runAction = async () => {
    setBusy(true);
    try {
      if (confirm === 'resend') {
        const { data } = await axios.post(`${API_URL}/email-marketing/campaigns/${id}/resend-failed`);
        toast.success(`Resending to ${data.count} recipients`);
      } else {
        await axios.post(`${API_URL}/email-marketing/campaigns/${id}/cancel`);
        toast.success('Sending stopped');
      }
      setConfirm(null);
      await loadStats();
      loadRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (!stats) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#2C9DE1]" /></div>;
  }

  const minutes = Math.ceil((toResend * SEND_DELAY_SECONDS) / 60);
  const leftMinutes = Math.ceil(((stats.pending_count || 0) * SEND_DELAY_SECONDS) / 60);

  return (
    <div className="space-y-6" data-testid="campaign-detail-page">
      <Button variant="ghost" className="-ml-3" onClick={() => navigate('/email-marketing?tab=history')} data-testid="campaign-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to campaigns
      </Button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="campaign-name">{stats.campaign_name}</h1>
          <p className="text-sm text-slate-500">{stats.subject}</p>
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <Badge className="capitalize" variant="outline" data-testid="campaign-status">{stats.status}</Badge>
            {stats.sent_at && <span>Sent {new Date(stats.sent_at).toLocaleString()}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {stats.status === 'sending' && (
            <Button variant="outline" className="text-red-600" onClick={() => setConfirm('stop')} data-testid="campaign-stop-btn">
              <StopCircle className="w-4 h-4 mr-2" /> Stop sending
            </Button>
          )}
          {!['sending', 'scheduled'].includes(stats.status) && toResend > 0 && (
            <Button className="bg-[#2C9DE1] hover:bg-[#1A7BB8]" onClick={() => setConfirm('resend')} data-testid="campaign-resend-failed-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Resend to failed ({toResend})
            </Button>
          )}
        </div>
      </div>

      {stats.status === 'sending' && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="campaign-sending-banner">
          <Clock className="w-4 h-4" />
          Sending one email every {SEND_DELAY_SECONDS} seconds — about {leftMinutes} minute{leftMinutes === 1 ? '' : 's'} left. You can close this page.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat value={stats.recipient_count} label="Recipients" className="bg-slate-50 text-slate-900" testId="stat-recipients" />
        <Stat value={stats.sent_count} label="Sent" className="bg-slate-50 text-slate-900" testId="stat-sent" />
        <Stat value={stats.opened_count} label={`Opened (${stats.open_rate}%)`} className="bg-green-50 text-green-600" testId="stat-opened" />
        <Stat value={stats.clicked_count} label={`Clicked (${stats.click_rate}%)`} className="bg-blue-50 text-blue-600" testId="stat-clicked" />
        <Stat value={stats.failed_count} label="Failed" className="bg-red-50 text-red-600" testId="stat-failed" />
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Recipients</h2>
            <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(1); }}>
              <SelectTrigger className="w-44" data-testid="campaign-recipient-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All recipients</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Not sent yet</SelectItem>
                <SelectItem value="opened">Opened</SelectItem>
                <SelectItem value="clicked">Clicked</SelectItem>
                <SelectItem value="not_opened">Not opened</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loadingRows ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows?.items?.length ? (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Opened</TableHead>
                    <TableHead className="text-center">Clicked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.items.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/students/${r.id}`)} data-testid={`campaign-recipient-${r.id}`}>
                      <TableCell>
                        <p className="font-medium text-sm">{r.name}</p>
                        <p className="text-xs text-slate-500">{r.email}</p>
                      </TableCell>
                      <TableCell><StatusCell r={r} /></TableCell>
                      <TableCell className="text-center">{r.opened ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <span className="text-slate-300">-</span>}</TableCell>
                      <TableCell className="text-center">{r.clicked ? <CheckCircle className="w-4 h-4 text-blue-500 mx-auto" /> : <span className="text-slate-300">-</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-sm text-slate-500 py-8" data-testid="campaign-recipients-empty">No recipients found</p>
          )}

          {rows?.total_pages > 1 && (
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span data-testid="campaign-recipients-count">Page {rows.page} of {rows.total_pages} · {rows.total} recipients</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="campaign-prev-page">Previous</Button>
                <Button size="sm" variant="outline" disabled={page >= rows.total_pages} onClick={() => setPage(page + 1)} data-testid="campaign-next-page">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === 'resend' ? `Resend to ${toResend} recipients?` : 'Stop sending this campaign?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'resend'
                ? `The campaign will be sent again only to people who didn't receive it. One email every ${SEND_DELAY_SECONDS} seconds — about ${minutes} minute${minutes === 1 ? '' : 's'}. People who already received it or unsubscribed are skipped.`
                : 'Emails already sent stay sent. You can resend to the rest later.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="campaign-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); runAction(); }} disabled={busy} data-testid="campaign-confirm-ok">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {confirm === 'resend' ? 'Resend' : 'Stop sending'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
